import * as THREE from 'three';
import { hash01 } from '@/core/rng';
import { expressed, type WildlifePhenotype } from '@/render/phenotype';

/**
 * Wildlife, seen (spec §45–§46). The simulation keeps head counts, not
 * animals; this module invents just enough body for a count to read as a
 * herd. Render-only; herd truth stays in the simulation.
 *
 * Each view scatters up to 64 stand-ins deterministically around its anchor
 * and drifts them on slow sine fields of the clock: a grazing walk, never a
 * jitter and never a teleport, and identical on every machine because nothing
 * here keeps state between frames.
 *
 * ---- WHAT IS SIMULATED AND WHAT IS ONLY DRAWN --------------------------
 *
 * Read this before believing anything a panel says about an animal.
 *
 * The SIMULATION knows five archetypes — grazer, browser, small game,
 * predator, scavenger — and nothing beneath them. `sim/wildlife/types.ts`
 * gives each one carrying capacity, growth and diet; there is no species
 * under the archetype, no individual anywhere, and no record of any animal.
 *
 * This module adds a VISUAL sub-kind (`SubKind`) and per-individual build,
 * both derived from `(herd seed, index)` by hash. A herd of grazers is drawn
 * as red deer or as wild horses; a stag carries antlers and a hind does not;
 * a calf is smaller and walks beside its mother. **None of that is simulated.**
 * Two herds of different sub-kind eat the same food at the same rate, breed
 * on the same curve and die to the same predators, because the archetype is
 * the only thing the simulation has. Any card, name or panel built on top of
 * this may say what an animal LOOKS like and must not say what it DOES
 * differently, until `sim/wildlife/` grows real species — which moves the
 * snapshot and is a serial commit of its own.
 *
 * `WildlifeView.phenotype` (2026-08-19) is the same honesty, one layer up: a
 * RENDER INPUT for the biological expression a future simulation will supply
 * — herd-level, normalized (`render/phenotype.ts`), absent everywhere today
 * and free when absent. It exists so trait truth has a visual endpoint
 * waiting; it does not make a drawn deer a simulated species, and nothing
 * here may imply a herd "genetically has" anything until `sim/` writes it.
 *
 * The honest half is still large: it is the difference between a hillside of
 * one stamp repeated and a hillside of animals.
 */

export type WildlifeSpecies =
  'grazer' | 'browser' | 'small_game' | 'predator' | 'scavenger';

export interface WildlifeView {
  species: WildlifeSpecies;
  /** Herd anchor; y is the fallback ground when no groundAt is supplied. */
  x: number; y: number; z: number;
  /** The census head count, whole. How much of it is drawn is this module's
   *  business — but the herd's SHAPE is read off this rather than off the
   *  drawn count, so approaching reveals more of the same herd standing where
   *  it stood and never re-deals it (law 8). */
  count: number;
  /** Stable per-view seed — same seed, same herd, always. */
  seed: number;
  /**
   * The census cell this view was dealt from, when it was dealt from one.
   * The anchor may LEAN out of its cell — the migration lean and the grazing
   * lap can carry it 0.16 of a cell past the edge, deliberately, so a herd is
   * found on the side of its ground that is filling — so `toSocial(x, z)`
   * would name the neighbour a sixth of the time at an edge. Anything that
   * asks the simulation about THIS herd (its ledger, its inclination, a cast
   * aimed at it) reads these. Optional: a test's hand-built view has no cell.
   */
  sx?: number;
  sz?: number;
  /**
   * The herd's EXPRESSED phenotype, when something supplies one
   * (`render/phenotype.ts`; 0 is ordinary, absent is ordinary and free).
   * RENDER INPUT ONLY, and the file header's honesty rule binds it doubly:
   * the simulation knows five archetypes and a head count, so nothing may
   * say "this herd genetically has X" — this field is where a future
   * biological simulation will SAY what a herd's bodies express, and until
   * one exists only the dev lab (`ui/phenoLab.ts`) ever sets it. Composes
   * beneath the sub-kind and above the per-body hashes: sub-kind baseline
   * × phenotype × seeded individual variation → body, per `bodyAt`.
   */
  phenotype?: Partial<WildlifePhenotype>;
}

/**
 * WILDLIFE PHENOTYPE EXPRESSION — the factor each axis buys at +1 on the
 * normalized scale (±2 is the clamp, so extremes are these squared). One
 * table for all archetypes: the quadruped is one architecture, and the axes
 * that are per-kind — appendages — gate themselves on the sub-kind's own
 * fields (`horn === 0` expresses nothing, whatever this says). Ranges are a
 * design range for future standing to spend, sized so +1 clears the
 * per-individual jitter it composes with (`test/phenotype.ts` §dominance).
 */
export const WILDLIFE_EXPRESSION = {
  stature: 1.22, bodyLength: 1.20, bodyMass: 1.22, legLength: 1.25,
  neckLength: 1.30, headScale: 1.25, appendageScale: 1.40,
  light: 1.35, warmth: 1.15,
} as const;

/**
 * The ARCHETYPE's nominal scale in metres — `SPEC[s].sx`, the number every
 * drawn body of that archetype is a multiple of. `tools/gait.ts` and
 * `tools/figures.ts` both depend on that identity in as many words.
 *
 * **It is a NOMINAL, not a bound, and the difference is load-bearing.** It was
 * documented here as an under-statement of every drawn body, and that was true
 * of the file it was written for: before per-individual size and the sub-kind
 * `sx` factor the smallest drawn grazer was 1.6 × 0.88 × 1.31 = 1.84 m, over
 * the 1.6 named here. It is no longer true in either direction. From
 * `tools/animals.ts` §5, which prints the body COUNT rather than describing it:
 * 3,000 herds of 64 head per archetype is 192,000 bodies for four of them and
 * 6,000 for predators, which draw alone or paired whatever the census holds.
 *
 *   archetype   bodies   drawn p10 / p50 / p90   smallest   this   under it
 *   grazer      192000      1.24 / 1.98 / 2.28    0.76 m     1.6      29.3%
 *   browser     192000      0.94 / 1.46 / 2.02    0.65 m     1.3      33.8%
 *   small_game  192000      0.29 / 0.47 / 0.55    0.22 m     0.4      22.7%
 *   predator      6000      1.71 / 1.89 / 2.09    1.61 m     1.5       0.0%
 *   scavenger   192000      0.45 / 0.64 / 0.75    0.27 m     0.5      20.1%
 *
 * The size × sub-kind multiplier this scale is spent through is the last
 * column of that same table: 0.385..1.392 on a grazer, i.e. −62% to +39% and
 * not the ±35% this comment used to claim, and 0.406..1.519 on a browser.
 *
 * So it OVER-states about a quarter of drawn bodies, and the ≥3 px
 * pointability gate is the consumer for which that direction is unsafe: at
 * 1.6 m the gate opens at 678 m, while the smallest grazer §5 measures — 0.76 m
 * — clears 3 px only at 324 m, leaving 354 m of range in which the pointer
 * would name an animal that is a pixel and a half. **`POINTABLE_LENGTH` below
 * is the bound that gate wants; this is not.**
 *
 * Why the values did not simply move down to the bound: `main.ts`'s
 * `WILD_REACH` multiplies the SAME table to decide how far a herd is drawn at
 * all, and there it wants a typical size, not a floor. `tools/wildlife.ts` §5
 * runs both tables through that rule from 300 m over 400 random land points
 * and prints them side by side — the eye goes from p10 23 / p50 74 / p90 261
 * bodies to p10 0 / p50 16 / p90 65, and from p10 2 / p50 4 / p90 7 kinds of
 * animal in sight to p10 0 / p50 2 / p90 3. That is the island emptying, which
 * is the complaint this whole lane exists to answer. One number cannot be both
 * the typical size and the minimum once individuals vary 3.6:1; the answer is
 * two numbers, not a compromise between them.
 */
export const BODY_LENGTH: Record<WildlifeSpecies, number> = {
  grazer: 1.6, browser: 1.3, small_game: 0.4, predator: 1.5, scavenger: 0.5,
};

const PERSONAL_SPACE = 120;  // m² of ground a grazing animal keeps to itself

/**
 * The RADIUS of the stand a herd of `head` holds, in metres. Grazing animals
 * keep a roughly constant personal distance, so a herd's ground goes as its
 * count and its radius as the square root — the island's densest grazer cell,
 * 68.3 head, stands on a 51 m radius, and two head on 9 m. A flat 180 m was
 * the old rule and it spread every herd, however small, over ten hectares:
 * `tools/wildlife.ts` measured the densest grazer cell covering 0.005% of its
 * own disc, which is not a herd but a scatter of dots. The same table in the
 * same probe now reads 2.000% for that cell — the rule and the drawn count
 * both moved, so read the old figure as history and the new one as shipped.
 *
 * Read off the census count, never off how many are drawn — see WildlifeView.
 */
export const herdSpread = (head: number): number =>
  Math.min(120, Math.max(6, Math.sqrt(Math.max(1, head) * PERSONAL_SPACE / Math.PI)));

const TAU = Math.PI * 2;

/**
 * Total stand-ins across all views, and the most any one view draws. 1,600 is
 * measured rather than chosen — on the densest island the old 420 could never
 * bind because the gate upstream could not deliver that many. See
 * `tools/wildlife.ts`.
 */
const CAP = 1600;
const PER_VIEW = 64;    // a herd reads as a herd well before this

/**
 * How many stand-ins a view draws: indices `0 .. bodiesInView - 1`.
 *
 * Omission is a PREFIX, which is law 8 for a herd. Approaching draws more of
 * the same animals standing where they stood; it never re-deals them, so body
 * 7 is body 7 at every range and a coarse view has simply left the tail off.
 */
export function bodiesInView(v: WildlifeView): number {
  // predators walk alone or paired, whatever the census says (spec §46)
  return v.species === 'predator'
    ? (v.count >= 2 ? 2 : v.count >= 1 ? 1 : 0)
    : Math.min(PER_VIEW, Math.floor(v.count));
}

/**
 * The furthest any body of a view can be from its anchor — the stand, plus
 * every metre of grazing drift, at the worst phase.
 *
 * Both drift terms are sinusoids of amplitude `drift` and `drift * 0.6`, so
 * each axis reaches `1.6 * drift` and the DIAGONAL reaches `1.6 * drift * √2`.
 * Dropping that √2 is easy and costs a real bug: the pointer's cheap reject
 * skips a herd whose anchor is further than this from the aim, so an
 * under-estimate makes exactly the animals that have wandered furthest — the
 * ones standing alone, away from the mass, and therefore the easiest to mean —
 * silently unpointable. `test/animals.ts` §5 is the tripwire.
 *
 * A calf does not widen this. It is placed beside its mother and TURNED about
 * her until it is inside the stand's own disc (`bodyAt`), so the bound is
 * unchanged and `test/visual-kits.ts`, which recomputes it by hand, still
 * holds. The turn replaced a radial clamp that kept the same bound and could
 * put a calf inside its mother; the reasoning is at the placement itself.
 */
export const standRadius = (v: WildlifeView): number => {
  const spread = v.species === 'predator'
    ? herdSpread(v.count) * 0.4 : herdSpread(v.count);
  return spread + Math.min(24, spread * 0.35) * 1.6 * Math.SQRT2;
};

/* ======================================================================
 * SUB-KINDS — the visual species under the archetype.
 * ====================================================================== */

/**
 * One drawable kind of animal. Every field is a multiplier on, or an angle
 * applied to, the single quadruped `quadruped()` builds; nothing here reaches
 * the simulation, and nothing here may (see the file header).
 *
 * The four form fields are exactly the four the measurement supports.
 * `tools/animals.ts` §4 prices every candidate feature of a drawn grazer in
 * pixels at the ranges a herd is actually looked at, and the answer is blunt:
 *
 *   horn tip above the back  reads to 448 m   ← the best shape signal there is
 *   horn beam                reads to 305 m
 *   leg length               reads to 299 m
 *   head travel to grazing   reads to 244 m
 *   tail                     reads to 231 m
 *   head above the back line reads to 173 m
 *   ear                      reads to  75 m   ← NOT BUILT, and this is why
 *   leg thickness            reads to  75 m   ← already ruled out, twice now
 *
 * So: horns, legs, head pitch and tail get per-instance geometry, and nothing
 * finer does. Ears were modelled and thrown away at 1.1 px at 200 m; antler
 * TINES were priced at ~2 px and thrown away for the same reason, which is
 * why a horn is one swept beam and not a rack. Everything below 3 px is
 * carried by the envelope (the three scale factors, which are free — they ride
 * the instance matrix that was already there) or by the coat.
 *
 * The same table says small game and scavengers get NO shape detail at all:
 * a 0.4 m rabbit's whole BODY reads to 222 m and its leg length to 75 m. They
 * still carry the fields, because the geometry is shared and a collapsed horn
 * costs nothing but a degenerate triangle — but the fields were chosen for
 * them on tone and proportion, and any pixel argument about them is a fiction.
 */
export interface SubKind {
  /** Stable id. Safe to persist, safe to key a card off. */
  id: string;
  /** What a player would call it. The naming module is free to ignore this. */
  label: string;
  /** Relative frequency among herds of this archetype. Sums are normalised. */
  weight: number;
  /** Envelope, multiplying the archetype's own scale. The weighted mean of the
   *  column below is within a few percent of 1 per archetype — sx 0.989 /
   *  0.976 / 0.970 / 1.008 / 1.017 for grazer, browser, small game, predator,
   *  scavenger — so the archetype scale stays the MIDDLE of the set. It is not
   *  the drawn length and never was: the unit body is 1.24–1.73 long, and
   *  against `tools/animals.ts` §5 a drawn median runs 1.12× (browser) to
   *  1.28× (scavenger) `BODY_LENGTH` while the smallest drawn body is about
   *  half of it — 0.48 / 0.50 / 0.55 / 1.07 / 0.54, the predator being the one
   *  row whose nominal is under every body it can draw. `POINTABLE_LENGTH` is
   *  the number that bounds them. */
  sx: number; sy: number; sz: number;
  /** Leg length, × the 0.44 hip height. Under 1 is short-legged and heavy. */
  leg: number;
  /** Resting head pitch, radians about +Z. Positive lifts the head. */
  neck: number;
  /** How far the head drops, in radians, when the animal is standing still.
   *  Zero for anything that does not put its mouth on the ground. */
  graze: number;
  /** Horn beam scale on an animal that carries one. 0 = the kind has none. */
  horn: number;
  /** Share of ADULTS that carry horns — sex, roughly. Calves never do. */
  horned: number;
  /** Tail scale. A wolf's is its silhouette; a boar's is not. */
  tail: number;
  /** Coat, linear RGB, before the per-individual jitter. */
  coat: readonly [number, number, number];
  /** Multiplier on the coat at the points — lower legs, face, tail, belly,
   *  rump. 1,1,1 is a plain animal; below 1 is dark-legged; above is pale. */
  points: readonly [number, number, number];
  /** The rate at which a calf is PROPOSED, not the share that results. A calf
   *  may not follow a calf (see `isCalf`, which is what keeps every mother an
   *  adult without a search), so the realised share is p(1−p) less the body at
   *  index 0, which is never one. `tools/animals.ts` §5 measures it over 30,016
   *  drawn bodies per kind — it prints that count, so this line can be checked
   *  rather than believed: deer 0.22 → 16.8%, horse 0.16 → 13.2%, aurochs 0.20
   *  → 15.9%, ibex 0.25 → 18.5%, elk 0.18 → 14.4%, boar 0.30 → 20.8%, roe 0.24
   *  → 17.9%, hare 0.20 → 15.6%, fowl 0.28 → 19.9%, marmot 0.22 → 16.8%,
   *  corvid 0.10 → 8.6%, fox 0.18 → 14.6%, striper 0.16 → 13.2%. Roughly
   *  20–25% under the field, and ANY panel or future sim lane that inherits
   *  this table must read it as the rate and not as the outcome.
   *  `tools/wildlife.ts` §5 prints the realised share per archetype beside the
   *  rest of the spread, so the two can never quietly diverge. */
  calves: number;
}

/**
 * The set. Sixteen drawn kinds against five simulated archetypes.
 *
 * Chosen per HERD, not per animal: a valley holds red deer and the next one
 * holds horses, which is what makes a sub-kind a thing a player can find,
 * remember and go back to. Mixed herds would have averaged all sixteen into
 * one grey mass at exactly the ranges §4 says the difference reads at.
 *
 * The weights are frequencies, not tuning: they say how often a flight meets
 * each kind, and `tools/wildlife.ts` §5 prints the realised mix over the
 * shipped island's 3,721 drawable herds so the intent and the outcome can be
 * compared. They are not equal, and the gap is sample size rather than bias:
 * the worst drift there is predator/wolf at 49.0% realised against 45.0%
 * intended, over 661 predator herds. `test/visual-herds.ts` §2 asks the other
 * question — whether `subKindOf` is an unbiased draw at all — over 20,000
 * synthetic seeds per archetype, where the worst is 0.8 points. Two samples,
 * two numbers; quoting either for the other is how "verified within 0.8
 * points" came to be said of an island that drifts 4.0.
 */
export const SUB_KINDS: Record<WildlifeSpecies, readonly SubKind[]> = {
  grazer: [
    { id: 'deer', label: 'red deer', weight: 0.34,
      sx: 1.00, sy: 1.02, sz: 0.92, leg: 1.10, neck: 0.0, graze: 1.15,
      horn: 1.00, horned: 0.45, tail: 0.35, calves: 0.22,
      coat: [0.62, 0.46, 0.30], points: [0.78, 0.74, 0.70] },
    { id: 'horse', label: 'wild horse', weight: 0.24,
      sx: 1.10, sy: 1.02, sz: 1.02, leg: 1.14, neck: -0.05, graze: 1.20,
      horn: 0, horned: 0, tail: 0.95, calves: 0.16,
      coat: [0.50, 0.38, 0.26], points: [0.50, 0.46, 0.44] },
    { id: 'aurochs', label: 'wild cattle', weight: 0.24,
      sx: 1.05, sy: 1.00, sz: 1.20, leg: 0.90, neck: -0.18, graze: 1.05,
      horn: 0.85, horned: 0.90, tail: 0.55, calves: 0.20,
      coat: [0.34, 0.27, 0.22], points: [1.25, 1.20, 1.12] },
    { id: 'ibex', label: 'wild goat', weight: 0.18,
      sx: 0.74, sy: 0.80, sz: 0.74, leg: 1.00, neck: 0.10, graze: 1.10,
      horn: 1.30, horned: 0.70, tail: 0.25, calves: 0.25,
      coat: [0.66, 0.60, 0.48], points: [0.62, 0.58, 0.55] },
  ],
  browser: [
    { id: 'elk', label: 'forest elk', weight: 0.32,
      sx: 1.20, sy: 1.16, sz: 1.00, leg: 1.18, neck: 0.12, graze: 0.75,
      horn: 1.25, horned: 0.40, tail: 0.18, calves: 0.18,
      coat: [0.30, 0.24, 0.18], points: [0.78, 0.76, 0.72] },
    { id: 'boar', label: 'wild boar', weight: 0.36,
      sx: 0.95, sy: 0.80, sz: 1.08, leg: 0.72, neck: -0.30, graze: 0.85,
      horn: 0.22, horned: 0.60, tail: 0.40, calves: 0.30,
      coat: [0.22, 0.19, 0.17], points: [1.45, 1.38, 1.28] },
    { id: 'roe', label: 'roe deer', weight: 0.32,
      sx: 0.78, sy: 0.82, sz: 0.76, leg: 1.08, neck: 0.08, graze: 1.05,
      horn: 0.55, horned: 0.45, tail: 0.15, calves: 0.24,
      coat: [0.55, 0.40, 0.26], points: [0.85, 0.82, 0.78] },
  ],
  small_game: [
    { id: 'hare', label: 'hare', weight: 0.42,
      sx: 1.05, sy: 0.95, sz: 0.92, leg: 1.15, neck: 0.20, graze: 0.90,
      horn: 0, horned: 0, tail: 0.20, calves: 0.20,
      coat: [0.58, 0.50, 0.40], points: [0.80, 0.78, 0.74] },
    { id: 'fowl', label: 'ground fowl', weight: 0.30,
      sx: 0.85, sy: 1.05, sz: 0.85, leg: 1.22, neck: 0.30, graze: 1.10,
      horn: 0, horned: 0, tail: 0.75, calves: 0.28,
      coat: [0.42, 0.36, 0.30], points: [0.55, 0.52, 0.60] },
    { id: 'marmot', label: 'marmot', weight: 0.28,
      sx: 0.98, sy: 0.82, sz: 1.05, leg: 0.70, neck: 0.05, graze: 0.95,
      horn: 0, horned: 0, tail: 0.35, calves: 0.22,
      coat: [0.55, 0.48, 0.42], points: [0.72, 0.68, 0.64] },
  ],
  predator: [
    { id: 'wolf', label: 'wolf', weight: 0.45,
      sx: 1.00, sy: 1.00, sz: 0.95, leg: 1.10, neck: -0.10, graze: 0,
      horn: 0, horned: 0, tail: 0.90, calves: 0,
      coat: [0.42, 0.40, 0.38], points: [0.66, 0.64, 0.62] },
    { id: 'greatcat', label: 'great cat', weight: 0.30,
      sx: 1.06, sy: 0.94, sz: 1.05, leg: 1.00, neck: -0.20, graze: 0,
      horn: 0, horned: 0, tail: 1.20, calves: 0,
      coat: [0.62, 0.52, 0.36], points: [0.72, 0.66, 0.58] },
    { id: 'bear', label: 'bear', weight: 0.25,
      sx: 0.96, sy: 1.30, sz: 1.26, leg: 0.78, neck: -0.28, graze: 0.45,
      horn: 0, horned: 0, tail: 0.12, calves: 0,
      coat: [0.26, 0.22, 0.19], points: [1.15, 1.10, 1.02] },
  ],
  scavenger: [
    { id: 'corvid', label: 'carrion bird', weight: 0.40,
      sx: 0.85, sy: 0.92, sz: 0.80, leg: 1.30, neck: 0.28, graze: 1.20,
      horn: 0, horned: 0, tail: 0.80, calves: 0.10,
      coat: [0.13, 0.13, 0.15], points: [1.10, 1.06, 1.15] },
    { id: 'fox', label: 'fox', weight: 0.34,
      sx: 1.12, sy: 0.96, sz: 0.92, leg: 1.02, neck: -0.05, graze: 0.35,
      horn: 0, horned: 0, tail: 1.15, calves: 0.18,
      coat: [0.48, 0.30, 0.18], points: [1.30, 1.24, 1.16] },
    { id: 'striper', label: 'striped scavenger', weight: 0.26,
      sx: 1.14, sy: 1.02, sz: 1.00, leg: 0.94, neck: -0.22, graze: 0.40,
      horn: 0, horned: 0, tail: 0.50, calves: 0.16,
      coat: [0.45, 0.40, 0.30], points: [0.55, 0.50, 0.46] },
  ],
};

/* ---- the hash salts ------------------------------------------------------
 * `hash01` is stateless, so a NEW salt cannot re-deal an existing draw the
 * way inserting a call into a shared generator would (law 1). 3, 5, 7 and 11
 * were already spoken for by the stand and the drift; everything added here
 * takes a salt above them and never reuses one, which is what keeps a
 * horn from correlating with a coat.
 */
const S_KIND = 101;      // which sub-kind a HERD is (herd-level, not per body)
const S_CALF = 13;       // calf or adult
const S_SIZE = 17;       // size within the class
const S_HORN = 19;       // does this adult carry horns, and how big
const S_TONE = 23;       // coat lightness
const S_HUE = 29;        // coat hue drift
const S_MORPH = 31;      // the rare coats
const S_POST = 37;       // how deeply this animal grazes
const S_CALFA = 41;      // where a calf stands relative to its mother
const S_CALFR = 43;
const S_MARK = 47;       // the points' own tone
const S_LEG = 53;        // leg length within the kind
const S_TAIL = 59;       // tail length within the kind

/** The row a herd-level hash is taken on — anything but a body index. */
const KIND_ROW = 9973;

/**
 * Which kind of animal a herd is. Pure in `(species, seed)`, so a herd is the
 * same kind for ever, on every machine, with nothing stored.
 *
 * Deliberately NOT a function of position, and this was tried and measured
 * before being dropped. **Do not add regional sub-kinds here; the evidence
 * that they do not work is below.**
 *
 * The idea is obviously attractive: the northern moors hold goats and the
 * river woods hold boar, so crossing a ridge changes the animals and finding a
 * range is a discovery. Two things kill it at this layer.
 *
 * First it cannot be done from the view's anchor at all. That anchor MOVES —
 * `main.ts` leans it toward whichever neighbouring cells are filling and walks
 * it a slow circuit, up to ~370 m, which is a third of a social cell — so any
 * spatial rule quantised finely enough to matter flips a herd from horses to
 * cattle as it crosses a boundary, in front of the player, mid-frame. That
 * needs a stable cell key passed down from `main.ts`.
 *
 * Second, and this is the one that settles it: even given a stable key it buys
 * almost nothing, because the island's herds are too dense for a range to be
 * legible. `tools/fauna.ts` §1b prices the rule over the shipped world's 3,721
 * drawable herds — two favoured kinds per range at 5× and 2× their weight
 * against 0.35× for the rest, drawn with the herd's own hash, and cross-checked
 * against `subKindOf` at boost 1 so the mirror cannot drift — and reports how
 * many of the sixteen kinds stand within reach of a random land point:
 *
 *   range size     kinds within 1 km      kinds within 2 km   island mix drift
 *   none (shipped)  p10 5 p50 9 p90 12     p10 12 p50 15 p90 16     4.0 points
 *   3 cells, 3 km   p10 4 p50 8 p90 11     p10 11 p50 14 p90 16     4.7 points
 *   6 cells, 6 km   p10 4 p50 8 p90 10     p10 10 p50 13 p90 15     6.1 points
 *   10 cells, 10 km p10 4 p50 8 p90 10     p10  9 p50 12 p90 15    11.5 points
 *
 * Fifteen kinds within two kilometres becomes twelve, and the price is that
 * the weight table above stops describing the island by up to eleven points —
 * against the 4.0 the shipped draw already drifts by on a sample this size.
 * A 2 km disc holds about fifty herds across five archetypes; no per-range
 * weighting survives that sample size. Real regional fauna has to come from
 * the thing that already knows what the ground is — `sim/habitat`, per cell,
 * per biome — and that is a simulation change, not a rendering one.
 *
 * What DOES give this island something to find, measured the same way, is the
 * rare individual: `PALE_RATE` puts the nearest pale animal a median 1.8 km
 * away and a p90 of 4.1 km, in a herd that otherwise looks like every other
 * herd of its kind. Discovery here is one animal, not one valley.
 */
export function subKindOf(species: WildlifeSpecies, seed: number): SubKind {
  const set = SUB_KINDS[species];
  let total = 0;
  for (const k of set) total += k.weight;
  let r = hash01(seed | 0, KIND_ROW, S_KIND) * total;
  for (const k of set) { r -= k.weight; if (r <= 0) return k; }
  return set[set.length - 1];
}

/**
 * Is body `i` a calf?
 *
 * Index 0 is never one, and neither is the body just after a calf — so a
 * calf's mother is always `i - 1` and is always an adult, with no recursion,
 * no search, and above all **no reading of the census count**. A rule that
 * needed the count would re-deal every animal the moment the herd grew, which
 * is law 8's failure mode and the thing `test/animals.ts` §3 watches for.
 */
function isCalf(seed: number, i: number, share: number): boolean {
  if (i <= 0 || share <= 0) return false;
  if (hash01(seed, i, S_CALF) >= share) return false;
  return !(i - 1 > 0 && hash01(seed, i - 1, S_CALF) < share);
}

/**
 * The rare coats — the reason to look twice at a herd you have seen before.
 *
 * Rates are measured, not chosen, and measured against a DISTANCE rather than
 * against a percentage — "one in nine hundred" says nothing on its own.
 * `tools/wildlife.ts` §5 sweeps candidates over the shipped island's 3,721
 * drawable herds and 57,074 drawn bodies:
 *
 *   1 in 200    287 pale, in 258 herds   nearest from random land, p50 0.94 km
 *   1 in 450    141 pale, in 130 herds                             p50 1.45 km
 *   1 in 900     80 pale, in  79 herds                             p50 1.84 km
 *   1 in 2000    32 pale, in  32 herds                             p50 3.00 km
 *   1 in 5000    16 pale, in  16 herds                             p50 4.42 km
 *
 * 1/200 puts one within a kilometre of anywhere a god happens to be standing,
 * which is not a find, it is a texture. 1/5000 is sixteen on a whole island
 * and reads as never. 1/900 gives eighty, a median of 1.8 km and a p90 of
 * 4.1 km — and since a pale grazer is 5.6 px at 400 m and 14.8 px at 150 m,
 * that is a distance to something a player finds by LOOKING, at an altitude
 * they have to choose, rather than something they fly past and notice.
 *
 * The dark morph is deliberately twice as common: a dark animal reads as
 * shadow at range and is the one you have to go and check.
 *
 * Visual only, and it must stay visual until the simulation has individuals:
 * a pale animal eats, breeds and dies exactly as its herd-mates do.
 */
const PALE_RATE = 1 / 900;
const DARK_RATE = 1 / 450;

export type CoatMorph = 'pale' | 'dark' | null;

/**
 * Per-ARCHETYPE scale — roughly metres of body length. The sub-kind multiplies
 * these; the coat used to live here too and now belongs to the sub-kind, which
 * is the whole point.
 */
const SPEC: Record<WildlifeSpecies, { sx: number; sy: number; sz: number }> = {
  grazer:     { sx: 1.6,  sy: 1.6,  sz: 1.6  },
  browser:    { sx: 1.3,  sy: 1.3,  sz: 1.3  },
  small_game: { sx: 0.4,  sy: 0.4,  sz: 0.4  },
  predator:   { sx: 1.5,  sy: 0.8,  sz: 0.95 },   // long-bodied
  scavenger:  { sx: 0.5,  sy: 0.5,  sz: 0.5  },
};

/**
 * The per-individual size classes, as multipliers on the archetype scale.
 *
 * Named rather than written inline because `POINTABLE_LENGTH` is DERIVED from
 * their floors. A mirrored 0.52 down there would go stale the first time this
 * line was tuned, and the failure would be silent in the one direction that
 * matters — a pointer offering an animal it can no longer bound.
 *
 * The top 3% of adults are a size class of their own. Not a flag, not a
 * rarity: the tail of one distribution, so nothing downstream can be tempted
 * to treat "big" as a kind (law 32 — a mythic thing is made by history).
 */
const CALF_LO = 0.52, CALF_SPAN = 0.16;
const ADULT_LO = 0.90, ADULT_SPAN = 0.22;
const BIG_TAIL = 0.97, BIG_SCALE = 1.13;
/** How far a calf stands from its mother, in metres — a calf's own length or
 *  two. Chosen, not measured; what is measured is that it is now the REALISED
 *  separation and not merely the dealt one (see `bodyAt`). */
const CALF_NEAR = 1.6, CALF_SPAN_M = 2.2;

/**
 * The unit body's nose-to-rump extent in x, tail excluded — the thing whose
 * width on screen decides whether one animal is a signal.
 *
 * Measured, not assumed: `tools/animals.ts` §5 pushes every vertex of the body
 * through `formVertex` for every sub-kind at every extreme of leg, horn, tail
 * and head pitch, and the extent runs 1.2432 (ground fowl, head down) to
 * 1.7331 (wild goat, horns up and grazing). 1.24 is the minimum rounded DOWN,
 * so everything built on it under-states a real body and never over-states one.
 * The 1.31 that `tools/animals.ts` §4 tabulates is the rest pose of the same
 * body; it is a fair average and the wrong number for a bound.
 */
export const NOSE_TO_RUMP = 1.24;

/**
 * A LOWER BOUND on the drawn nose-to-rump length of any body of an archetype,
 * in metres — what a ≥3 px pointability gate has to read when all it holds is
 * the herd. A pick that reads `BODY_LENGTH` there is the defect this exists to
 * end, and a pick that already holds the body should ask `drawnLength`
 * instead; `test/animals.ts` §6 prices all three against each other.
 *
 * `BODY_LENGTH` cannot serve: it over-states about a quarter of drawn bodies
 * (see its comment), and over-stating is the direction that makes an animal
 * nameable while it is a pixel and a half of nothing. This is the other
 * direction, and it is the safe one — the pointer offers an individual later
 * than it becomes visible, never earlier.
 *
 * DERIVED from the sub-kind table and the size classes rather than written
 * down, which is the whole point: a sub-kind added tomorrow with an `sx` of
 * 0.4 moves this number by itself. A constant copied here by hand is how the
 * bound broke the first time. `tools/animals.ts` §5 re-derives it independently
 * from what `bodyAt` actually returns and `test/animals.ts` §8 fails if the two
 * ever part, so neither can drift alone.
 *
 * At the shipped table: grazer 0.76, browser 0.65, small_game 0.22,
 * predator 1.61, scavenger 0.27 m. The ≥3 px gate on a 46°/1080 px stage
 * therefore opens at 324 / 277 / 93 / 681 / 116 m, against 678 / 551 / 170 /
 * 636 / 212 m for the nominal — and predator is the row that moves OUTWARD,
 * because 1.5 was already under every predator the table can draw.
 */
export const POINTABLE_LENGTH: Record<WildlifeSpecies, number> = (() => {
  const out = {} as Record<WildlifeSpecies, number>;
  for (const s of Object.keys(SPEC) as WildlifeSpecies[]) {
    let min = Infinity;
    for (const k of SUB_KINDS[s]) {
      // a kind that bears no calves cannot deal one, so its floor is an adult's
      const floor = k.calves > 0 ? CALF_LO : ADULT_LO;
      min = Math.min(min, SPEC[s].sx * k.sx * floor * NOSE_TO_RUMP);
    }
    out[s] = min;
  }
  return out;
})();

/**
 * Hold the coat × points product under white. Module scope, not a closure
 * inside `bodyAt`, and that is not style: `bodyAt` runs for every drawn body
 * every frame, so declaring this inside it allocates 1,600 closures a frame.
 * Interleaved A/B, best of eight runs of 200 full-cap frames — 0.435 ms
 * hoisted against 0.691 ms as a closure, a 59% tax on the whole herd update
 * for two divisions. It was only noticed because the change was benchmarked
 * against the previous file rather than assumed to be free.
 *
 * That A/B is the one number in this file with no probe behind it: the closure
 * form no longer exists to measure against, and both figures are a machine and
 * an afternoon. What IS re-runnable is the path they were measured on —
 * `tools/wildlife.ts` §4 times the same full-cap `update` end to end and prints
 * it (0.549 ms here, best of 5×200), so the scale can be checked even though
 * the comparison cannot. Read the 59% as the reason for the shape of the code,
 * not as a figure to quote.
 */
const capAlbedo = (c: number, m: number): number =>
  (c > 1e-3 ? Math.min(m, 0.99 / c) : m);

/** One animal, as the renderer places it — and as a card would read it. */
export interface AnimalBody {
  x: number; y: number; z: number;
  /** Drift velocity — the analytic derivative, so heading needs no history. */
  vx: number; vz: number;
  /** Per-animal size multiplier; individuals differ a lot more than they did. */
  scale: number;
  /** Its own phase, which the gait rides. */
  phase: number;
  /** How hard it is walking, 0..1 — sets the trot and lifts the head. */
  effort: number;
  /** Final instance scale, archetype × sub-kind × individual × phenotype. */
  sx: number; sy: number; sz: number;
  /** The four per-instance form parameters the vertex shader consumes. */
  leg: number; horn: number; tail: number; pitch: number;
  /** The phenotype form factors — neck length, head scale, body mass —
   *  exactly 1 for a view with no phenotype. They travel to the shader in
   *  aGait.w (neck·head, packed — see `packNeckHead`) and aMark.w (mass). */
  neckK: number; headK: number; massK: number;
  /** Coat, and the multiplier applied at the points. Flat scalars rather than
   *  arrays on purpose: `update` calls this for every drawn body every frame,
   *  and the two arrays this replaced were 3,200 allocations a frame. */
  cr: number; cg: number; cb: number;
  mr: number; mg: number; mb: number;
  /** What it is, and what is unusual about it. Nothing is stored for these. */
  kind: SubKind;
  calf: boolean;
  morph: CoatMorph;
}

/**
 * How long THIS body is drawn, nose to rump, in metres.
 *
 * The exact answer for the individual, where `POINTABLE_LENGTH` is only the
 * archetype's floor — so a gate that has the body in hand should ask this and
 * a gate that has only the herd should ask that. `main.ts`'s pick already
 * calls `bodyAt`, so it has the body in hand and this costs it one multiply.
 *
 * Conservative by construction: `NOSE_TO_RUMP` is the minimum extent over the
 * whole table, so a full-grown aurochs measures 2.94 m here against the 3.31 m
 * its own horns-up pose actually spans. Under-stating is the safe side (see
 * `BODY_LENGTH`), and the alternative is running `formVertex` over 246
 * vertices per animal per frame to save a quarter of a pixel.
 */
export const drawnLength = (b: AnimalBody): number => b.sx * NOSE_TO_RUMP;

/**
 * Where body `i` of a view stands at `tSec`, and what it looks like.
 *
 * Pure, and it is the renderer's own arithmetic rather than a copy of it —
 * `update()` below calls this for every instance it draws. That matters
 * because the POINTER now resolves individual animals through the same
 * function: if the two ever disagreed, the game would name an animal standing
 * somewhere other than where the player can see it, and nothing would fail.
 *
 * Identity comes free from it, exactly as a tree's does. A tree needs no id
 * because the world is a pure function of position; an animal needs none
 * because its body is a pure function of `(herd seed, index, clock)` — so
 * `(seed, i)` IS its name, the same on every machine, for ever, with nothing
 * stored and nothing allocated for being pointed at. Everything the panel
 * would want to say about it — kind, size, age, horns, coat — comes out of
 * the same pure call and is therefore free in exactly the same sense.
 */
export function bodyAt(
  v: WildlifeView, i: number, tSec: number,
  groundAt?: (x: number, z: number) => number,
): AnimalBody {
  const seed = v.seed | 0;
  const kind = subKindOf(v.species, seed);
  /**
   * The herd's phenotype, expressed — factors of exactly 1 when nothing
   * supplies one, which is every herd in the shipped world, so the neutral
   * path multiplies by one and the body is bit-identical. Composed HERE, at
   * the herd grain, beneath the per-body hashes: a +1 population's smallest
   * individual still clears a neutral population's largest on the same axis
   * (the dominance rule; `test/phenotype.ts` measures it).
   */
  let fStat = 1, fLen = 1, fMass = 1, fLeg = 1, fNeck = 1, fHead = 1, fApp = 1, fLight = 1, fWarm = 1;
  const pt = v.phenotype;
  if (pt) {
    const E = WILDLIFE_EXPRESSION;
    fStat = expressed(pt.stature ?? 0, E.stature);
    fLen = expressed(pt.bodyLength ?? 0, E.bodyLength);
    fMass = expressed(pt.bodyMass ?? 0, E.bodyMass);
    fLeg = expressed(pt.legLength ?? 0, E.legLength);
    fNeck = expressed(pt.neckLength ?? 0, E.neckLength);
    fHead = expressed(pt.headScale ?? 0, E.headScale);
    fApp = expressed(pt.appendageScale ?? 0, E.appendageScale);
    fLight = expressed(pt.light ?? 0, E.light);
    fWarm = expressed(pt.warmth ?? 0, E.warmth);
  }
  // the stand's own size, from the census — so it does not breathe as the eye
  // approaches and more of the herd is drawn (law 8)
  const spread = v.species === 'predator'
    ? herdSpread(v.count) * 0.4 : herdSpread(v.count);
  // grazing drift is a fraction of the stand, not a flat 32 m: on a 9 m stand
  // the old amplitude walked every animal clean out of its herd
  const drift = Math.min(24, spread * 0.35);

  const calf = isCalf(seed, i, kind.calves);
  // A calf's drift is its MOTHER'S drift, term for term, so the two never
  // separate: it is the offset that makes them two animals, not the field.
  // Its gait phase is still its own, or a herd of pairs would trot in lockstep.
  const dIdx = calf ? i - 1 : i;
  const h1 = hash01(seed, i, 3), h2 = hash01(seed, i, 5);
  const h3 = hash01(seed, dIdx, 7), h4 = hash01(seed, dIdx, 11);

  // home stand: sqrt keeps the scatter even over the disc, not centre-heavy
  let hx: number, hz: number;
  if (calf) {
    const pa = hash01(seed, dIdx, 3) * TAU, pr = Math.sqrt(hash01(seed, dIdx, 5)) * spread;
    const a2 = hash01(seed, i, S_CALFA) * TAU;
    const r2 = CALF_NEAR + hash01(seed, i, S_CALFR) * CALF_SPAN_M;
    // Keep the calf inside the stand's disc by TURNING it about its mother, not
    // by pulling it toward the anchor — so `standRadius` is untouched and the
    // separation is exactly `r2`, whatever the herd's size.
    //
    // The radial clamp this replaces scaled the calf's position about the
    // ANCHOR, which on a small stand walks it into its mother. `tools/animals.ts`
    // §6 runs both rules over the same 47,763 mother/calf pairs on stands of
    // 2..300 head — the replaced one from the same hashes, cross-checked
    // against `bodyAt`'s own stand before it is believed — and reports:
    //
    //   rule                       min     p50    under the 1.6 m dealt   inside her
    //   shipped (turn)           1.600   2.698           0.00%              0.00%
    //   replaced (radial clamp)  0.035   2.665           1.37%              0.23%
    //
    // A calf standing inside its mother is not a subtle defect; it was invisible
    // because `test/visual-herds.ts` §6 asserted only the 4 m upper bound.
    //
    // The turn is exact rather than iterative. With the mother at radius `pr`
    // and the offset at `r2`, |mother + offset|² = pr² + r2² + 2·pr·r2·cos θ,
    // so the calf is inside the disc exactly while cos θ ≤ C below. C ≥ −1
    // always — that would need |pr − r2| > spread, and `herdSpread` floors the
    // stand at 6 m against a 3.8 m maximum offset — so a legal angle always
    // exists and the smallest turn onto it is all this does.
    let th = a2 - pa;
    if (pr > 1e-9) {
      const C = (spread * spread - pr * pr - r2 * r2) / (2 * pr * r2);
      if (C < 1) {
        const lim = Math.acos(C < -1 ? -1 : C);
        th -= TAU * Math.round(th / TAU);              // to (−π, π]
        if (th < lim && th > -lim) th = th < 0 ? -lim : lim;
      }
    }
    const a3 = pa + th;
    hx = Math.cos(pa) * pr + Math.cos(a3) * r2;
    hz = Math.sin(pa) * pr + Math.sin(a3) * r2;
  } else {
    const a = h1 * TAU, r = Math.sqrt(h2) * spread;
    hx = Math.cos(a) * r; hz = Math.sin(a) * r;
  }

  // grazing drift: two slow incommensurate sine fields, pure in tSec — top
  // speed well under a metre a second, so the herd walks, never blinks
  const ph = h3 * TAU;
  const w1 = 0.016 + h4 * 0.008, w2 = 0.01 + h3 * 0.005;
  const dx = Math.sin(tSec * w1 + ph) * drift
           + Math.sin(tSec * w2 + ph * 1.9) * drift * 0.6;
  const dz = Math.cos(tSec * w1 * 0.93 + ph * 1.3) * drift
           + Math.sin(tSec * w2 * 1.21 + ph * 0.7) * drift * 0.6;
  const x = v.x + hx + dx, z = v.z + hz + dz;
  const vx = w1 * Math.cos(tSec * w1 + ph) * drift
           + w2 * Math.cos(tSec * w2 + ph * 1.9) * drift * 0.6;
  const vz = -w1 * 0.93 * Math.sin(tSec * w1 * 0.93 + ph * 1.3) * drift
           + w2 * 1.21 * Math.cos(tSec * w2 * 1.21 + ph * 0.7) * drift * 0.6;

  // ---- the build -------------------------------------------------------
  // Age first, because everything else reads off it. A calf is a little over
  // half its mother's length and leggier for it, which is what makes a herd
  // with young in it read as a herd with young in it rather than as a herd
  // drawn at two scales.
  const sh = hash01(seed, i, S_SIZE);
  // The classes, and their floors, are declared beside `POINTABLE_LENGTH`,
  // which is derived from them — see the note there about mirrored constants.
  const big = !calf && sh > BIG_TAIL ? BIG_SCALE : 1;
  const size = calf ? CALF_LO + sh * CALF_SPAN : (ADULT_LO + sh * ADULT_SPAN) * big;
  const arch = SPEC[v.species];
  // stature scales the whole animal; length stretches it along the spine;
  // mass widens the stance (the barrel's own depth and the legs' thickness
  // ride `massK` in the shader, so mass never buys height)
  const sx = arch.sx * kind.sx * size * fStat * fLen;
  const sy = arch.sy * kind.sy * size * fStat;
  const sz = arch.sz * kind.sz * size * fStat * fMass;

  const hh = hash01(seed, i, S_HORN);
  // appendage expression multiplies what the kind and the individual already
  // carry: a hornless kind's 0, and a hind's 0, stay exactly 0 — the honest
  // no-op — while a stag's beam grows or shrinks with the population's
  const horn = (calf || kind.horn <= 0 || hh >= kind.horned
    ? 0 : kind.horn * (0.62 + (hh / Math.max(1e-6, kind.horned)) * 0.55)) * fApp;

  // Head down while it stands, up while it moves. `effort` is the same term
  // the trot rides, so an animal that is walking is also looking up — one
  // number, two signals, and the herd reads as busy rather than as posed.
  const effort = Math.min(1, Math.hypot(vx, vz) / 0.5);
  const pitch = kind.neck - kind.graze * (1 - effort) * (0.55 + hash01(seed, i, S_POST) * 0.45);

  // Coat. Tone and hue drift separately: a herd whose members differ only in
  // brightness reads as one animal under different light, which is the defect
  // this replaces (every grazer on the island was one colour × ±12%).
  //
  // These two ranges are CHOSEN, not measured, and they are the only chosen
  // numbers in this file — everything else came off `tools/animals.ts` §4 or
  // `tools/wildlife.ts` §5. What constrains them is a measurement of the thing
  // they replace: the old rule spanned 1.27× in luma inside one herd and had
  // no hue variation whatever, so a replacement that lands near 1.27× has not
  // happened. `test/visual-herds.ts` §8 measures the realised span over one
  // 40-head grazer herd — it was 1.36× on the first cut, which is that defect
  // with extra steps, and is 1.56× now — and fails under 1.45×, which is the
  // guard against quietly tuning back.
  //
  // Calves are lighter, which is true of most of these animals and does a
  // second job: it is what makes young VISIBLE in a herd at the range the herd
  // is seen from, and therefore what makes "this herd is breeding" a thing a
  // player can notice rather than a thing a panel would have to say.
  const tone = calf
    ? 1.02 + hash01(seed, i, S_TONE) * 0.26
    : 0.80 + hash01(seed, i, S_TONE) * 0.44;
  const hue = hash01(seed, i, S_HUE);
  // phenotype pigment sits UNDER the rare morphs: a population can run pale
  // or warm, and an albino born into a dark population still reads as the
  // absolute outlier it is (the morph mixes toward its own targets after)
  let cr = kind.coat[0] * tone * (0.94 + hue * 0.14) * fLight * fWarm;
  let cg = kind.coat[1] * tone * fLight;
  let cb = kind.coat[2] * tone * (1.06 - hue * 0.14) * fLight / fWarm;
  const mo = hash01(seed, i, S_MORPH);
  let morph: CoatMorph = null;
  if (mo < PALE_RATE) {
    morph = 'pale';
    cr += (0.94 - cr) * 0.88; cg += (0.93 - cg) * 0.88; cb += (0.90 - cb) * 0.88;
  } else if (mo < PALE_RATE + DARK_RATE) {
    morph = 'dark';
    cr *= 0.34; cg *= 0.34; cb *= 0.36;
  }
  // Albedo may never exceed 1, and two of these multiply. A pale morph of a
  // kind whose points are PALER than its coat — wild cattle at 1.25, boar at
  // 1.45 — lands the product past white on the belly and rump. It is not the
  // emissive class of bug (this is instance colour on a MeshStandardMaterial,
  // so it goes dark at night with everything else) but it is an albedo
  // brighter than white, which blows out at midday exactly where the
  // screenshot gate looks. Capped here rather than in the shader so the CPU
  // side and `test/visual-herds.ts` can both see it.
  cr = Math.min(cr, 0.98); cg = Math.min(cg, 0.98); cb = Math.min(cb, 0.98);
  const mj = 0.94 + hash01(seed, i, S_MARK) * 0.12;
  // Its OWN gait phase, even when it borrows its mother's drift — a herd of
  // pairs trotting in lockstep is the tell that the two were fused.
  const ownPh = calf ? hash01(seed, i, 7) * TAU : ph;
  return {
    x, z, y: groundAt ? groundAt(x, z) : v.y,
    vx, vz, scale: size, phase: ownPh, effort,
    sx, sy, sz,
    // legLength composes with the calf's own gangliness and the individual
    // jitter — so a long-legged juvenile is a long-legged JUVENILE, never a
    // full-size adult: the existing calf scaling is untouched underneath
    leg: kind.leg * (calf ? 1.07 : 1) * (0.96 + hash01(seed, i, S_LEG) * 0.08) * fLeg,
    horn, tail: kind.tail * (0.85 + hash01(seed, i, S_TAIL) * 0.3), pitch,
    neckK: fNeck, headK: fHead, massK: fMass,
    cr, cg, cb,
    mr: capAlbedo(cr, kind.points[0] * mj),
    mg: capAlbedo(cg, kind.points[1] * mj),
    mb: capAlbedo(cb, kind.points[2] * mj),
    kind, calf, morph,
  };
}

export interface Wildlife {
  group: THREE.Group;
  /**
   * `groundAt` resolves terrain height at each animal's final drifted
   * position — the scatter is up to 120 m, and on a slope the anchor's y would
   * float or bury the herd's edges. Omit it and the anchor y is used.
   */
  update(tSec: number, views: WildlifeView[], camR: number,
         groundAt?: (x: number, z: number) => number): void;
}

// The budget. One instanced draw call of an 82-triangle body, against a frame
// that already carries 4.7 M triangles in 656 calls, so this is rounding error
// and was never the reason herds were invisible: measured on the shipped
// island, the old 420 could not bind, because the gate upstream could never
// deliver even that many. See `tools/wildlife.ts`.
// CAP and PER_VIEW are declared above, beside `bodiesInView` which is what
// they actually govern now.
const VIS = 4500;       // beyond this, herds are census, not sight

/**
 * The body, and why it is shaped the way it is. Forward is +x.
 *
 * ONE geometry, still, in ONE instanced draw call — `test/visual-kits.ts`
 * counts every animal on the island in a single mesh named `wildlife`, and a
 * mesh per sub-kind would have been sixteen draw calls and sixteen budgets to
 * split. The shape differences ride per-instance attributes instead: three
 * scale factors on the matrix that was already there, and four form
 * parameters (`aForm`) the vertex shader applies before the gait.
 *
 * History, because it keeps being relearnt. This was a box body over a
 * **leg-block** — one solid slab standing in for all four legs — and read as a
 * brick on a plinth; `tools/gait.ts` priced a grazer's legs at 4.1 px at 150 m
 * and 15.3 px at 40 m and four real legs went in. The vertical proportions
 * were then divided wrong (53% of shoulder height in the barrel) and came back
 * a box on stubby legs; legs are 58% of shoulder height, which is a red deer's
 * actual ratio, and `LEG_TOP` is that.
 *
 * What is new, and what it cost:
 *
 * | part          | tris | why, in pixels (tools/animals.ts §4, grazer)   |
 * |---------------|-----:|------------------------------------------------|
 * | barrel        |   12 | the envelope; reads to 889 m                    |
 * | four legs     |   32 | leg LENGTH reads to 299 m; thickness to 75 m    |
 * | neck          |    8 | carries the head; 204 m on its own              |
 * | head          |   12 | 176 m, but it is what the pitch moves            |
 * | horns, a pair |   12 | tip above the back reads to 448 m — the best     |
 * | tail          |    6 | 231 m, and it is what says predator              |
 *
 * 82 triangles against 64. At the 1,600-body cap that is 131 k triangles,
 * 2.8% of a frame that already carries 4.7 M.
 *
 * TWO earlier rulings are reversed here, and both reversals carry evidence:
 *
 *   · "no tail — it reads only inside 40 m". That was true of the tail that
 *     was tried; it is not true of a tail sized like an animal's. At 0.34 of
 *     body length a grazer's tail is 0.54 m and reads to 231 m, and a
 *     predator's at 1.15 is its single most identifying feature at range.
 *   · "a tail would push the bounding box past the envelope the herd maths was
 *     measured against". It does — pushed through `formVertex` at rest the unit
 *     body now runs −0.656 to 0.825 in x, against −0.480 to 0.825 with the tail
 *     left out, which is the extent §4 tabulates — and the tail is the
 *     harmless half of that
 *     change. The consequential half was per-individual size and the sub-kind
 *     `sx` factor, which arrived in the same commit and broke the property
 *     this bullet used to claim: `BODY_LENGTH` was an under-statement of every
 *     drawn body and is now an over-statement of about a quarter of them. Do
 *     not "fix" `BODY_LENGTH` upward, and do not read it as a bound either —
 *     `POINTABLE_LENGTH` and `drawnLength` are the bounds, and the argument
 *     for keeping all three is written at `BODY_LENGTH` itself.
 *
 * Ears stay unbuilt: 1.1 px at 200 m, 2.8 px at 80 m, i.e. never a signal at
 * any range a herd is looked at. Antler tines the same, at ~2 px.
 *
 * The neck is longer than it was (0.30 against 0.22) and the head sits further
 * out and up, entirely so that the head PITCH has somewhere to travel. With
 * the old neck the head could only swing 0.25 m — 1.6 px at 200 m, under the
 * bar and therefore not worth a shader instruction. With this one it swings
 * 0.58 m, 3.7 px at 200 m, and grazing reads.
 */
/** Which form rule a vertex obeys. The GLSL compares against these numbers. */
export const PART = {
  barrel: 0, leg: 1, neck: 2, head: 3, horn: 4, tail: 5,
} as const;

interface Part {
  geo: THREE.BufferGeometry;
  /** Gait sign: which diagonal pair this leg trots with. 0 = does not swing. */
  swing: number;
  /** Gait pivot — the hip. */
  joint: [number, number, number];
  /** Which form rule applies: 0 barrel, 1 leg, 2 neck, 3 head, 4 horn, 5 tail. */
  part: number;
  /** This part's own scaling origin, for the parts `aForm` scales. */
  pivot: [number, number, number];
  /** How strongly a vertex takes the second coat colour, as f(position). */
  mark: (x: number, y: number, z: number) => number;
}

// Shoulder height and the leg geometry. LEG_TOP is 58% of shoulder height,
// which is a red deer's ratio — see the history above.
const LEG_TOP = 0.44;      // hip and shoulder height; the barrel sits on it
const LEG_X = 0.30;        // fore and aft, inside the body's own footprint
const LEG_Z = 0.14;        // left and right
// The pivot the whole head end swings about: the base of the neck.
const SHOULDER_X = 0.468, SHOULDER_Y = 0.700;
// Where a horn leaves the skull, and where a tail leaves the rump.
const HORN_X = 0.66, HORN_Y = 1.00, HORN_Z = 0.055;
const TAIL_X = -0.46, TAIL_Y = 0.66;
// The neck's own axis and length, for the phenotype's neckK: the neck
// cylinder is built along +y and turned `rotateZ(-0.70)`, so its axis runs
// (sin 0.70, cos 0.70) in the xy plane, 0.30 long, out of the shoulder — the
// head rides exactly the elongation the neck's far end takes, so the two can
// never part (the §4 mirror holds the gap through the whole phenotype range).
const NECK_DIR_X = Math.sin(0.70), NECK_DIR_Y = Math.cos(0.70);
const NECK_LEN = 0.30;
// The barrel's centre height, for massK's depth term, and the head box's
// centre, the pivot headK scales about.
const BARREL_CY = 0.60;
const HEAD_CX = 0.695, HEAD_CY = 0.935;

function quadruped(): THREE.BufferGeometry {
  const parts: Part[] = [
    // the barrel — the feature that carries to 889 m. Its mark weight is
    // countershading: a box's four corners interpolate across the flank, so a
    // pale belly costs no vertices and gives a real gradient rather than a
    // band. Rear corners take a little more, which is a rump patch.
    { swing: 0, joint: [0, 0, 0], part: 0, pivot: [0, 0, 0],
      mark: (x, y) => Math.min(1, (y < 0.60 ? 0.55 : 0) + (x < -0.3 ? 0.35 : 0)),
      geo: new THREE.BoxGeometry(1.0, 0.32, 0.40).translate(0.02, 0.60, 0) },
    // neck: leans up and forward out of the shoulder, and longer than it was
    // so the head pitch has room to travel
    { swing: 0, joint: [0, 0, 0], part: 2, pivot: [SHOULDER_X, SHOULDER_Y, 0],
      mark: () => 0.15,
      geo: new THREE.CylinderGeometry(0.09, 0.075, 0.30, 4, 1, true)
        .rotateZ(-0.70).translate(0.565, 0.815, 0) },
    // head
    { swing: 0, joint: [0, 0, 0], part: 3, pivot: [0.695, 0.935, 0],
      mark: () => 0.85,
      geo: new THREE.BoxGeometry(0.26, 0.16, 0.15).translate(0.695, 0.935, 0) },
    // tail: one tapered spike off the rump, hanging back and down
    { swing: 0, joint: [0, 0, 0], part: 5, pivot: [TAIL_X, TAIL_Y, 0],
      mark: (x, y) => (y < 0.55 ? 1 : 0.4),
      geo: new THREE.CylinderGeometry(0.012, 0.030, 0.34, 3, 1, true)
        .rotateZ(2.5592).translate(-0.5535, 0.518, 0) },
  ];
  // Horns: a swept beam a side, no tines — a tine was priced at ~2 px at
  // 200 m. `aForm.y` scales them about the root, so 0 collapses a pair to a
  // point on the skull and a hind costs the same twelve degenerate triangles
  // a stag pays for its antlers.
  for (const hz of [HORN_Z, -HORN_Z]) {
    parts.push({
      swing: 0, joint: [0, 0, 0], part: 4, pivot: [HORN_X, HORN_Y, hz],
      mark: () => 1,
      geo: new THREE.CylinderGeometry(0.014, 0.034, 0.45, 3, 1, true)
        .rotateZ(0.35).translate(HORN_X - 0.0772, HORN_Y + 0.2114, hz),
    });
  }
  // Four legs, trotting: the diagonal pairs move together, which is the gait a
  // walking quadruped actually uses and the one the eye names as "animal" at
  // range. Front-left swings with rear-right, front-right with rear-left.
  // Mark runs 1 at the hoof to 0.25 at the hip: dark or pale socks.
  for (const fx of [LEG_X, -LEG_X]) for (const fz of [LEG_Z, -LEG_Z]) {
    parts.push({
      swing: Math.sign(fx) * -Math.sign(fz),
      joint: [fx, LEG_TOP, fz],
      part: 1, pivot: [fx, 0, fz],
      mark: (_x, y) => 1 - 0.75 * (y / LEG_TOP),
      geo: new THREE.CylinderGeometry(0.055, 0.04, LEG_TOP, 4, 1, true)
        .translate(fx, LEG_TOP / 2, fz),
    });
  }

  const flat = parts.map((p) => ({ ...p, geo: p.geo.getIndex() ? p.geo.toNonIndexed() : p.geo }));
  const total = flat.reduce((s, p) => s + p.geo.attributes.position.count, 0);
  const pos = new Float32Array(total * 3), nor = new Float32Array(total * 3);
  const swing = new Float32Array(total), joint = new Float32Array(total * 3);
  const part = new Float32Array(total), pivot = new Float32Array(total * 3);
  const mark = new Float32Array(total);
  let v = 0;
  for (const p of flat) {
    const src = p.geo.attributes.position.array as Float32Array;
    pos.set(src, v * 3);
    nor.set(p.geo.attributes.normal.array as Float32Array, v * 3);
    for (let i = 0; i < p.geo.attributes.position.count; i++) {
      swing[v + i] = p.swing;
      joint[(v + i) * 3] = p.joint[0];
      joint[(v + i) * 3 + 1] = p.joint[1];
      joint[(v + i) * 3 + 2] = p.joint[2];
      part[v + i] = p.part;
      pivot[(v + i) * 3] = p.pivot[0];
      pivot[(v + i) * 3 + 1] = p.pivot[1];
      pivot[(v + i) * 3 + 2] = p.pivot[2];
      mark[v + i] = p.mark(src[i * 3], src[i * 3 + 1], src[i * 3 + 2]);
    }
    v += p.geo.attributes.position.count;
  }
  new Set([...parts, ...flat].map((p) => p.geo)).forEach((g) => g.dispose());

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setAttribute('swing', new THREE.BufferAttribute(swing, 1));
  geo.setAttribute('joint', new THREE.BufferAttribute(joint, 3));
  geo.setAttribute('part', new THREE.BufferAttribute(part, 1));
  geo.setAttribute('pivot', new THREE.BufferAttribute(pivot, 3));
  geo.setAttribute('markW', new THREE.BufferAttribute(mark, 1));
  return geo;
}

/**
 * The individual's build, on the CPU — **a line-for-line mirror of `formPos`
 * in the vertex shader thirty lines below, and it lives here so that nobody
 * can edit one without seeing the other.**
 *
 * The whole build happens on the GPU, because a herd is one instanced draw
 * call and there is exactly one geometry to deform. That leaves the most
 * consequential code in this file invisible to every headless test: a shader
 * that folds an animal inside out fails nothing. This function is how the
 * contract gets asserted anyway — `test/visual-herds.ts` §4 pushes every
 * vertex of the body through it for every sub-kind and checks the things that
 * would be bugs (hooves through the ground, a tail through the ground, a
 * hornless animal with a nub on its skull, a head detached from its neck).
 *
 * It is NOT called at runtime. If you change the shader, change this, and
 * then look at a herd in a browser — the mirror can only catch the errors it
 * was taught to look for.
 *
 * @param p     vertex position in object space, from the geometry
 * @param part  its `PART` id
 * @param pivot its part's own scaling origin
 * @param form  (leg length, horn scale, tail scale, head pitch) — `aForm`
 * @param form2 (neck length, head scale, body mass, 0) — the phenotype
 *              channel (aGait.w/aMark.w on the GPU, plain factors here);
 *              defaults to its exact neutral, so every caller written before
 *              phenotype existed means what it meant.
 *              Every form2 term below is additive (`p += (p − c)·(k − 1)`),
 *              which at k = 1 adds a literal zero — bit-exact neutrality,
 *              the same argument the plant morph makes.
 */
export function formVertex(
  p: readonly [number, number, number],
  part: number,
  pivot: readonly [number, number, number],
  form: readonly [number, number, number, number],
  form2: readonly [number, number, number, number] = [1, 1, 1, 0],
): [number, number, number] {
  const [lg, hornS, tailS, pitch] = form;
  const [neckK, headK, massK] = form2;
  const lift = LEG_TOP * (lg - 1);
  let [x, y, z] = p;
  // legs stretch up from the ground; every other part rides the hip
  if (part === PART.leg) {
    y *= lg;
    // body mass: a leg thickens about its own axis, never lengthens
    x += (x - pivot[0]) * (massK - 1);
    z += (z - pivot[2]) * (massK - 1);
  } else y += lift;
  // body mass: the barrel deepens about its own centre — the belly drops and
  // the back rises a little; height stays the legs' business
  if (part === PART.barrel) {
    const bc = BARREL_CY + lift;
    y += (y - bc) * (massK - 1);
  }
  // horns and tail scale about their own root — 0 collapses them to it
  if (part === PART.horn || part === PART.tail) {
    const k = part === PART.horn ? hornS : tailS;
    const rx = pivot[0], ry = pivot[1] + lift, rz = pivot[2];
    x = rx + (x - rx) * k; y = ry + (y - ry) * k; z = rz + (z - rz) * k;
  }
  // neck length: the neck elongates along its own axis out of the shoulder…
  if (part === PART.neck) {
    const d = (x - SHOULDER_X) * NECK_DIR_X + (y - (SHOULDER_Y + lift)) * NECK_DIR_Y;
    x += NECK_DIR_X * d * (neckK - 1);
    y += NECK_DIR_Y * d * (neckK - 1);
  }
  // …and the head — horns with it — rides exactly the far end's travel, then
  // takes its own scale about its (shifted) centre
  if (part === PART.head || part === PART.horn) {
    const shX = NECK_DIR_X * NECK_LEN * (neckK - 1);
    const shY = NECK_DIR_Y * NECK_LEN * (neckK - 1);
    x += shX; y += shY;
    const hcx = HEAD_CX + shX, hcy = HEAD_CY + lift + shY;
    x += (x - hcx) * (headK - 1);
    y += (y - hcy) * (headK - 1);
    z += z * (headK - 1);
  }
  // the head end pitches about the shoulder: grazing, level or alert
  if (part >= PART.neck && part <= PART.horn) {
    const jx = SHOULDER_X, jy = SHOULDER_Y + lift;
    const rx = x - jx, ry = y - jy;
    const c = Math.cos(pitch), s = Math.sin(pitch);
    x = jx + c * rx - s * ry; y = jy + s * rx + c * ry;
  }
  return [x, y, z];
}

/**
 * The phenotype channel travels in SPARE COMPONENTS, not a new attribute —
 * and the reason is a GPU limit a headless suite cannot see. This program was
 * already at the driver's vertex-attribute ceiling (7 per-vertex + the
 * instance matrix's four + colour + three vec4s), and a dedicated `aForm2`
 * linked with **"Too many attributes"** on a stock MacBook GL — every animal
 * on the island silently gone, all tests green. So `aGait` and `aMark` widen
 * to vec4 (a vec4 costs the same one attribute location a vec3 does) and
 * carry the three factors in their w's: `aMark.w` is massK plain, `aGait.w`
 * packs neckK and headK fixed-point (k×1000 each, 12 bits apart — factors
 * live in [0.5, 1.7], so ±0.001 of a factor is sub-millimetre on a 2 m body,
 * and the packed integer stays far under float32's exact range). NEUTRAL IS
 * STILL EXACT: 1000·4096 + 1000 decodes to exactly 1.0 twice, and every use
 * is additive. `packNeckHead`/`PACK_NEUTRAL` are the one encoder, shared by
 * `update` and the tests.
 */
export const packNeckHead = (neckK: number, headK: number): number =>
  Math.round(neckK * 1000) * 4096 + Math.round(headK * 1000);
const PACK_NEUTRAL = packNeckHead(1, 1);

export function createWildlife(): Wildlife {
  const group = new THREE.Group();
  const geo = quadruped();
  // Per-instance gait: phase, rate, amplitude — and the packed neck/head
  // phenotype in w (see packNeckHead). Same device as the mortals' walk —
  // three 0.160 has no instanced skinning, and a herd has to stay one draw
  // call, so the legs turn in the vertex shader instead of on a skeleton.
  const gaitAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
  gaitAttr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aGait', gaitAttr);
  const gait = gaitAttr.array as Float32Array;
  for (let i = 0; i < CAP; i++) gait[i * 4 + 3] = PACK_NEUTRAL;
  // Per-instance build: (leg length, horn scale, tail scale, head pitch). The
  // whole of §4's measured feature list, in one vec4.
  const formAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
  formAttr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aForm', formAttr);
  const form = formAttr.array as Float32Array;
  // Per-instance second coat: what the points, belly and rump are multiplied
  // by — and the phenotype's massK in w. One vec4 buys dark socks, a pale
  // rump, countershading and a heavier body.
  const markAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
  markAttr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aMark', markAttr);
  const markI = markAttr.array as Float32Array;
  for (let i = 0; i < CAP; i++) {
    markI[i * 4] = 1; markI[i * 4 + 1] = 1; markI[i * 4 + 2] = 1; markI[i * 4 + 3] = 1;
  }

  const material = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });
  const gaitTime = { value: 0 };
  /*
   * Legs swing fore-and-aft, which with forward at +x is a rotation about Z —
   * the mortal's is about X for the same reason, its forward being +z.
   *
   * `formPos` is the individual's build, applied before the gait. It is
   * deliberately four `if`s over a per-vertex constant and no branching on
   * anything per-instance, so every vertex of a body takes the same path.
   *
   * THE ONE THING TO KNOW BEFORE EDITING: this is the half of the build that
   * no headless test can see. `test/visual-herds.ts` asserts the CPU half —
   * that `bodyAt` hands out the form parameters it should, that they are pure,
   * that they stay inside the ranges below — and it cannot assert that this
   * shader spends them correctly. If a change here makes animals fold inside
   * out, the tests will still be green. Verify it in a browser at 200-400 m
   * over a camp at midday, per CLAUDE.md, or do not claim it.
   *
   * The ranges are the ones `bodyAt` can actually produce over the whole
   * sub-kind table; `test/visual-herds.ts` §3 sweeps every kind and asserts
   * them, so a new sub-kind that widened one would fail rather than clip.
   *
   *   aForm.x  leg length   0.67 .. 1.45   scales legs from the GROUND up, so
   *                                        the hoof stays planted and the hip
   *                                        rises; everything else rides the lift
   *   aForm.y  horn scale   0    .. 1.55   about the horn root; 0 collapses it
   *   aForm.z  tail scale   0    .. 1.40   about the tail root
   *   aForm.w  head pitch  -1.25 .. +0.30  radians about +Z at the shoulder;
   *                                        negative puts the mouth in the grass
   *
   * Those are the ranges of a herd with NO phenotype — every herd today. A
   * supplied `WildlifeView.phenotype` multiplies aForm.x and aForm.y beyond
   * them (by at most `WILDLIFE_EXPRESSION`'s gains at the ±2 clamp: legs
   * ×0.64..×1.56, horns ×0.51..×1.96) and drives aForm2 = (neck, head, mass)
   * in ×0.59..×1.69 each; `test/phenotype.ts` sweeps the composed extremes
   * through the `formVertex` mirror instead of these constants.
   */
  const compileBody = (sh: THREE.WebGLProgramParametersWithUniforms) => {
    sh.uniforms.uGaitTime = gaitTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        uniform float uGaitTime;
        attribute float swing;
        attribute vec3 joint;
        attribute float part;
        attribute vec3 pivot;
        attribute float markW;
        attribute vec4 aGait;
        attribute vec4 aForm;
        attribute vec4 aMark;
        // aGait = (phase, rate, amplitude in radians, packed neck·head)
        float gaitAngle() {
          return sin(uGaitTime * aGait.y + aGait.x) * aGait.z * swing;
        }
        vec3 gaitRot(vec3 p, vec3 j, float a) {
          vec3 r = p - j;
          float c = cos(a), s = sin(a);
          return j + vec3(c * r.x - s * r.y, s * r.x + c * r.y, r.z);
        }
        // the individual's build — see the comment above this shader. The
        // phenotype factors ride aGait.w (neckK·headK packed, see packNeckHead)
        // and aMark.w (massK); every use is ADDITIVE, so the neutral values
        // add a literal zero and deform nothing.
        vec3 formPos(vec3 p) {
          float lg = aForm.x;
          float lift = ${LEG_TOP.toFixed(4)} * (lg - 1.0);
          float qn = floor(aGait.w / 4096.0);
          float neckK = qn / 1000.0;
          float headK = (aGait.w - qn * 4096.0) / 1000.0;
          float massK = aMark.w;
          // legs stretch up from the ground; every other part rides the hip
          if (part == 1.0) {
            p.y *= lg;
            // body mass: a leg thickens about its own axis, never lengthens
            p.xz += (p.xz - pivot.xz) * (massK - 1.0);
          } else { p.y += lift; }
          // body mass: the barrel deepens about its own centre
          if (part == 0.0) {
            float bc = ${BARREL_CY.toFixed(4)} + lift;
            p.y += (p.y - bc) * (massK - 1.0);
          }
          // horns and tail scale about their own root — 0 collapses them to it
          if (part == 4.0 || part == 5.0) {
            vec3 r = vec3(pivot.x, pivot.y + lift, pivot.z);
            p = r + (p - r) * (part == 4.0 ? aForm.y : aForm.z);
          }
          // neck length: the neck elongates along its own axis…
          if (part == 2.0) {
            vec2 nd = vec2(${NECK_DIR_X.toFixed(6)}, ${NECK_DIR_Y.toFixed(6)});
            float d = dot(p.xy - vec2(${SHOULDER_X.toFixed(4)}, ${SHOULDER_Y.toFixed(4)} + lift), nd);
            p.xy += nd * d * (neckK - 1.0);
          }
          // …and the head (horns with it) rides its far end, then takes its
          // own scale about its shifted centre
          if (part == 3.0 || part == 4.0) {
            vec2 sh = vec2(${NECK_DIR_X.toFixed(6)}, ${NECK_DIR_Y.toFixed(6)}) * ${NECK_LEN.toFixed(4)} * (neckK - 1.0);
            p.xy += sh;
            vec2 hc = vec2(${HEAD_CX.toFixed(4)}, ${HEAD_CY.toFixed(4)} + lift) + sh;
            p.xy += (p.xy - hc) * (headK - 1.0);
            p.z += p.z * (headK - 1.0);
          }
          // the head end pitches about the shoulder: grazing, level or alert
          if (part >= 2.0 && part <= 4.0) {
            p = gaitRot(p, vec3(${SHOULDER_X.toFixed(4)}, ${SHOULDER_Y.toFixed(4)} + lift, 0.0), aForm.w);
          }
          return p;
        }`)
      .replace('#include <color_vertex>', `#include <color_vertex>
        #ifdef USE_INSTANCING_COLOR
          vColor.xyz *= mix(vec3(1.0), aMark.rgb, markW);
        #endif`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        // the leg stretch is y-only on cylinders whose side normals are
        // horizontal, so it leaves them exact and only the two rotations move
        if (part >= 2.0 && part <= 4.0) objectNormal = gaitRot(objectNormal, vec3(0.0), aForm.w);
        if (abs(swing) > 0.001) objectNormal = gaitRot(objectNormal, vec3(0.0), gaitAngle());`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        transformed = formPos(transformed);
        if (abs(swing) > 0.001)
          transformed = gaitRot(transformed, vec3(joint.x, joint.y * aForm.x, joint.z), gaitAngle());`);
  };
  material.onBeforeCompile = compileBody;
  /*
   * The shadow pass draws the SAME body (2026-08-18). A depth material of its
   * own, compiled through the identical vertex code, so a grazing head and a
   * swinging leg cast the shape they are — three's stock depth material would
   * cast the un-built, un-gaited template. `<color_vertex>` is not in the depth
   * shader, so that replacement is a no-op there, which is what it should be.
   */
  const bodyDepth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  bodyDepth.onBeforeCompile = compileBody;

  const mesh = new THREE.InstancedMesh(geo, material, CAP);
  mesh.name = 'wildlife';
  mesh.frustumCulled = false;
  mesh.count = 0;
  // A herd on the ground casts onto it and takes the tree shade it grazes in
  // (2026-08-18) — the animals were the one drawn thing that neither cast nor
  // received, and read as decals for it.
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.customDepthMaterial = bodyDepth;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  // setColorAt allocates lazily — do it now so update never allocates
  const white = new THREE.Color(1, 1, 1);
  for (let i = 0; i < CAP; i++) mesh.setColorAt(i, white);
  mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
  group.add(mesh);

  // per-frame scratch — the update path allocates nothing
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();

  return {
    group,
    update(tSec, views, camR, groundAt) {
      gaitTime.value = tSec;
      mesh.visible = camR < VIS && views.length > 0;
      if (!mesh.visible) { mesh.count = 0; return; }
      let n = 0;
      for (const v of views) {
        const reps = bodiesInView(v);
        for (let i = 0; i < reps && n < CAP; i++, n++) {
          // ONE implementation, shared with the pointer — see `bodyAt`. The
          // pick resolves individual animals through this same call, so the
          // game cannot name an animal standing anywhere but where it is drawn,
          // and cannot name it as anything but what it is drawn as.
          const b = bodyAt(v, i, tSec, groundAt);
          _p.set(b.x, b.y, b.z);
          // face down the drift: the analytic derivative, so heading needs no history
          _e.set(0, Math.atan2(-b.vz, b.vx), 0);
          _q.setFromEuler(_e);
          // The trot rides the drift the animal is already doing, off the same
          // derivative that set the heading — so a leg never scuffs backwards
          // against ground it is supposed to be pushing off. Amplitude is
          // measured, not chosen: `tools/gait.ts` puts stride reach at 0.30 of
          // body length, which across a 0.36 leg is asin(0.15 / 0.36) ≈ 0.43
          // rad. Standing still is not frozen — 0.06 rad of weight shift, far
          // under a pixel at range, and the thing that stops a grazing herd
          // reading as a scatter of furniture up close.
          gait[n * 4] = b.phase;
          gait[n * 4 + 1] = 1.0 + b.effort * 3.0;
          gait[n * 4 + 2] = 0.06 + b.effort * 0.37;
          // the packed phenotype: exactly PACK_NEUTRAL for every herd today
          gait[n * 4 + 3] = b.neckK === 1 && b.headK === 1
            ? PACK_NEUTRAL : packNeckHead(b.neckK, b.headK);
          form[n * 4] = b.leg;
          form[n * 4 + 1] = b.horn;
          form[n * 4 + 2] = b.tail;
          form[n * 4 + 3] = b.pitch;
          markI[n * 4] = b.mr; markI[n * 4 + 1] = b.mg; markI[n * 4 + 2] = b.mb;
          markI[n * 4 + 3] = b.massK;
          _s.set(b.sx, b.sy, b.sz);
          _m.compose(_p, _q, _s);
          mesh.setMatrixAt(n, _m);
          _c.setRGB(b.cr, b.cg, b.cb);
          mesh.setColorAt(n, _c);
        }
      }
      mesh.count = n;
      mesh.instanceMatrix.needsUpdate = true;
      gaitAttr.needsUpdate = true;
      formAttr.needsUpdate = true;
      markAttr.needsUpdate = true;
      mesh.instanceColor!.needsUpdate = true;
    },
  };
}
